import { Persona, PersonaVideo, PersonaVideoAd } from "../models";
import { fetchAuthenticated } from "../scripts/custom-fetch";
import {PersonaData} from "../models/personaData";
import { withCompanyScope } from "../scripts/companyScope";

export async function createPersona(persona: Omit<Persona, "id" | "pageConfig" | "promptConfig" | "status">) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(persona),
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as Persona;
}

export async function updatePersona(personaId: number, persona: Partial<Omit<Persona, "id" | "uuid">>) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/${personaId}`), {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(persona),
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }
}

export async function updatePersonaLinks(linkingChanges: any[]) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/linkingChanges`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(linkingChanges),
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }
}

export async function copyPersona(personaId: number) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/${personaId}/copy`), {
    method: "POST",
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as Persona;
}

export async function getPersonas() {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_GET}/personas`));

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as Persona[];
}

export type PersonaSearchParams = {
  page: number;
  pageSize: number;
  text: string;
  types: string[];
  tagIds: number[];
  deleted: boolean;
  company: "all" | "unassigned" | number;
  sortKey: "createdAt" | "id" | "name" | "profileName" | "type" | "company";
  sortDirection: "asc" | "desc";
};

export async function searchPersonas(params: PersonaSearchParams) {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
    text: params.text,
    types: params.types.join(","),
    deleted: String(params.deleted),
    company: String(params.company),
    sortKey: params.sortKey,
    sortDirection: params.sortDirection,
  });

  if (params.tagIds.length > 0) {
    query.set("tagIds", params.tagIds.join(","));
  }

  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_GET}/personas/search?${query}`));

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as { personas: Persona[], total: number, totalPages: number };
}

export async function getTags() {
  const response = await fetchAuthenticated(`${process.env.REACT_APP_API_GET}/tag/getTags`);

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json();
}

export async function getPersona(personaId: number, refresh: boolean) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_GET}/personas/${personaId}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({refresh: refresh}),
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as PersonaData;
}

export async function linkVideo(personaId: number, videoId: number, link: { order: number, locked: boolean }) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/${personaId}/videos/${videoId}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(link),
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }
}

export async function unlinkVideo(personaId: number, videoId: number) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/${personaId}/videos/${videoId}`), {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }
}

export async function getPersonaVideos(personaId: number) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_GET}/personas/${personaId}/videos`));

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as PersonaVideo[];
}

export async function linkVideoAd(personaId: number, videoAdId: number, link: { order: number, locked: boolean }) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/${personaId}/video-ads/${videoAdId}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(link),
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }
}

export async function unlinkVideoAd(personaId: number, videoAdId: number) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_POST}/personas/${personaId}/video-ads/${videoAdId}`), {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(await response.json());
  }
}

export async function getPersonaVideoAds(personaId: number) {
  const response = await fetchAuthenticated(withCompanyScope(`${process.env.REACT_APP_API_GET}/personas/${personaId}/video-ads`));

  if (!response.ok) {
    throw new Error(await response.json());
  }

  return await response.json() as PersonaVideoAd[];
}
